import {
  Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner,
} from '@heroui/react';
import { AxiosError } from 'axios';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useHeroToast } from '@/hooks';
import { useGetAllRoomServices } from '@/views/RoomService/queries';

import { useClaimKdsDevice } from '../../queries';
import * as S from './PairKdsDeviceModal.styles';

const CODE_LENGTH = 6;

/** Kod z ekranu tabletu — te same znaki, co w alfabecie backendu. */
const CODE_PATTERN = /^[ACDEFGHJKMNPQRTUVWXY34679]{6}$/;

const sanitizeCode = (raw: string): string => raw.toUpperCase().replace(/[^ACDEFGHJKMNPQRTUVWXY34679]/g, '').slice(0, CODE_LENGTH);

interface PairKdsDeviceModalProps {
  isOpen: boolean;
  hotelId: string;
  onClose: () => void;
  onPaired: () => void;
}

/**
 * Modal parowania ekranu KDS. Pracownik przepisuje kod wyświetlony na tablecie,
 * nadaje ekranowi nazwę i opcjonalnie wiąże go z jednym punktem gastronomicznym
 * (przy jednej restauracji wybór jest zbędny, więc pole się wtedy nie pokazuje).
 */
export const PairKdsDeviceModal: FC<PairKdsDeviceModalProps> = ({
  isOpen, hotelId, onClose, onPaired,
}) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useHeroToast();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [roomServiceId, setRoomServiceId] = useState('');
  const [error, setError] = useState('');

  const { data: roomServices = [] } = useGetAllRoomServices(hotelId);
  const claimDevice = useClaimKdsDevice();

  useEffect(() => {
    if (isOpen) {
      setCode('');
      setName('');
      setRoomServiceId('');
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!CODE_PATTERN.test(code)) {
      setError(t('myDevices.kds.errors.invalidCode', 'Kod ma sześć znaków — przepisz go z ekranu tabletu.'));
      return;
    }
    setError('');

    claimDevice.mutate(
      {
        hotelId,
        code,
        name: name.trim() || undefined,
        roomServiceId: roomServiceId || null,
      },
      {
        onSuccess: () => {
          showSuccess(t('myDevices.kds.pairSuccess', 'Ekran został przypisany do hotelu.'));
          onPaired();
          onClose();
        },
        onError: (err: AxiosError) => {
          // Backend odsyła klucz błędu w `message` koperty v3.
          const key = (err.response?.data as { message?: string } | undefined)?.message ?? '';
          if (key === 'kdsDevice.codeNotFound') {
            setError(t(
              'myDevices.kds.errors.codeNotFound',
              'Nie znaleziono takiego kodu albo już wygasł. Odśwież kod na tablecie i spróbuj ponownie.',
            ));
            return;
          }
          if (key === 'kdsDevice.roomServiceNotFound') {
            setError(t('myDevices.kds.errors.roomServiceNotFound', 'Wybrany punkt gastronomiczny nie istnieje.'));
            return;
          }
          showError(t('myDevices.kds.errors.generic', 'Nie udało się przypisać ekranu.'));
        },
      },
    );
  };

  const isLoading = claimDevice.isPending;

  return (
    <Modal isOpen={isOpen} onClose={() => (isLoading ? undefined : onClose())} size="md">
      <ModalContent>
        <ModalHeader>{t('myDevices.kds.pairTitle', 'Dodaj ekran KDS')}</ModalHeader>
        <ModalBody>
          <S.Description>
            {t(
              'myDevices.kds.pairDescription',
              'Uruchom aplikację KDS na tablecie i przepisz kod, który pokaże się na jego ekranie.',
            )}
          </S.Description>

          <S.Field>
            <S.Label htmlFor="kds-code">{t('myDevices.kds.codeLabel', 'Kod z tabletu')}</S.Label>
            <S.CodeInput
              id="kds-code"
              value={code}
              disabled={isLoading}
              placeholder="ACDEFG"
              autoComplete="off"
              spellCheck={false}
              maxLength={CODE_LENGTH}
              onChange={(e) => setCode(sanitizeCode(e.target.value))}
              autoFocus
            />
          </S.Field>

          <S.Field>
            <S.Label htmlFor="kds-name">{t('myDevices.kds.nameLabel', 'Nazwa ekranu')}</S.Label>
            <S.TextInput
              id="kds-name"
              value={name}
              disabled={isLoading}
              maxLength={80}
              placeholder={String(t('myDevices.kds.namePlaceholder', 'np. Kuchnia główna'))}
              onChange={(e) => setName(e.target.value)}
            />
          </S.Field>

          {roomServices.length > 1 && (
            <S.Field>
              <S.Label htmlFor="kds-outlet">
                {t('myDevices.kds.outletLabel', 'Punkt gastronomiczny')}
              </S.Label>
              <S.Select
                id="kds-outlet"
                value={roomServiceId}
                disabled={isLoading}
                onChange={(e) => setRoomServiceId(e.target.value)}
              >
                <option value="">{t('myDevices.kds.outletAll', 'Wszystkie punkty')}</option>
                {roomServices.map((outlet) => (
                  <option key={outlet.id} value={outlet.id}>{outlet.name}</option>
                ))}
              </S.Select>
              <S.Hint>
                {t(
                  'myDevices.kds.outletHint',
                  'Ekran pokaże tylko zamówienia z wybranego punktu.',
                )}
              </S.Hint>
            </S.Field>
          )}

          {error && <S.ErrorText>{error}</S.ErrorText>}
        </ModalBody>
        <ModalFooter>
          <S.GhostButton type="button" onClick={onClose} disabled={isLoading}>
            {t('common.cancel', 'Anuluj')}
          </S.GhostButton>
          <S.PrimaryButton type="button" onClick={handleSubmit} disabled={isLoading}>
            {isLoading && <Spinner size="sm" />}
            {t('myDevices.kds.pairConfirm', 'Przypisz ekran')}
          </S.PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
